import type { ComponentWithReplacement } from '../types';
import type { ComponentMaintenanceData } from '../../../../Helpers/maintenanceCostUtils';

/** Shown in the bulk card where the selected components' values differ */
export const MIXED_VALUE = 'Blandade';

export type DraftEditMode = 'single' | 'multi';

/** The specificType a selection is locked to: that of the first ticked component */
export const selectionType = (plans: ComponentWithReplacement[]): string | null =>
    plans[0]?.component.specificType?.value ?? null;

/**
 * Whether a component can be ticked: it has a specificType, and the selection
 * (if any) is of that type. Components without a type share no activity rows.
 */
export const canSelect = (plan: ComponentWithReplacement, lockedType: string | null): boolean => {
    const type = plan.component.specificType?.value;
    if (!type) return false;
    return lockedType === null || type === lockedType;
};

/**
 * The keys a subsection's select-all checkbox governs: the components of the
 * locked type, or, with nothing ticked yet, of the first typed component's type
 * (an "Alla" subsection may mix types).
 */
export const selectableKeysOf = (components: ComponentWithReplacement[], lockedType: string | null): string[] => {
    const type = lockedType ?? components.find(c => c.component.specificType?.value)?.component.specificType?.value ?? null;
    if (!type) return [];
    return components.filter(c => c.component.specificType?.value === type).map(c => c.component.key);
};

/** The one value when every entry is the same, else `MIXED_VALUE` */
export const unanimous = (values: string[]): string =>
    values.length > 0 && values.every(v => v === values[0]) ? values[0] : MIXED_VALUE;

/** The one value when every entry is the same, else null */
export const unanimousOrNull = <T>(values: T[]): T | null =>
    values.length > 0 && values.every(v => v === values[0]) ? values[0] : null;

/** The numeric sum of display cost strings ('' counts as 0); '' when nothing has a cost */
export const sumDisplayCosts = (values: string[]): string => {
    const total = values.reduce((sum, v) => sum + (v ? parseInt(v, 10) || 0 : 0), 0);
    return total > 0 ? total.toString() : '';
};

/**
 * The keys a done/exclude click acts on: those not yet in the state when the
 * selection is mixed or off (so one click brings every component along), all
 * of them when every component already is (the click switches them off).
 */
export const keysToToggle = (
    keys: string[],
    draft: Record<string, ComponentMaintenanceData | undefined>,
    flag: 'isDone' | 'excluded'
): string[] => {
    const off = keys.filter(key => draft[key]?.[flag] !== true);
    return off.length > 0 ? off : keys;
};

/** How many of the selected components carry an entry for the row */
export const rowPresence = (
    keys: string[],
    draft: Record<string, ComponentMaintenanceData | undefined>,
    templateId: string
): { present: number; total: number } => ({
    present: keys.filter(key => draft[key]?.maintenanceActivities?.[templateId] !== undefined).length,
    total: keys.length
});

/** The selected components that have the row; every one of them when none has it yet */
export const rowTargets = (
    keys: string[],
    draft: Record<string, ComponentMaintenanceData | undefined>,
    templateId: string
): string[] => {
    const withRow = keys.filter(key => draft[key]?.maintenanceActivities?.[templateId] !== undefined);
    return withRow.length > 0 ? withRow : keys;
};
